/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FileText, Download, Lock, Package, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';

const documents = [
  { name: 'Guide utilisateur', desc: 'Projets, taches, sprints, jalons, BI', file: 'Planigo_Guide_Utilisateur.pdf', type: 'PDF', color: 'red', roles: ['admin', 'project_manager', 'developer', 'tester', 'business_analyst', 'director', 'viewer'] },
  { name: "Guide d'installation", desc: 'Installation VPS native, services systemd, sauvegarde', file: 'Planigo_Guide_Installation.pdf', type: 'PDF', color: 'red', roles: ['admin'] },
  { name: 'Guide administrateur', desc: 'Parametrage, roles, audit, RGPD et API Qlik Sense', file: 'Planigo_Guide_Administrateur.pdf', type: 'PDF', color: 'red', roles: ['admin'] },
];

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
};

export default function DocumentationCard() {
  const { user, isAdmin, hasPermission } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [packageInfo, setPackageInfo] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [packageError, setPackageError] = useState('');
  const canGenerate = isAdmin || hasPermission?.('admin:settings');

  const visibleDocs = documents.filter(doc => {
    if (isAdmin) return true;
    return doc.roles.includes(user?.role);
  });

  const loadPackageInfo = async () => {
    if (!canGenerate) return;
    try {
      const response = await axios.get(`${API}/api/admin/deployment/package-info`, { withCredentials: true });
      setPackageInfo(response.data);
    } catch (error) {
      // Keep documentation usable even if package history cannot be read.
    }
  };

  useEffect(() => { loadPackageInfo(); }, [canGenerate]); // eslint-disable-line react-hooks/exhaustive-deps

  const generatePackage = async () => {
    setGenerating(true);
    setPackageError('');
    try {
      const response = await axios.post(`${API}/api/admin/deployment/package`, {}, { withCredentials: true, timeout: 950000 });
      const latest = response.data;
      setPackageInfo(prev => ({ ...(prev || {}), version: latest.version, latest }));
      window.location.href = `${API}${latest.download_url}`;
    } catch (error) {
      setPackageError(error?.response?.data?.detail || error?.message || 'Generation du package impossible');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="card-surface" data-testid="documentation-section">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" />Base documentaire & deploiement</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {visibleDocs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {visibleDocs.map((doc, i) => (
              <a key={i} href={`${API}/api/files/${doc.file}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors" data-testid={`doc-${doc.file}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${doc.color === 'red' ? 'bg-red-100' : 'bg-blue-100'}`}>
                  <span className={`text-[10px] font-bold ${doc.color === 'red' ? 'text-red-700' : 'text-blue-700'}`}>{doc.type}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                  <p className="text-[10px] text-slate-500">{doc.desc}</p>
                </div>
                <Download className="w-4 h-4 text-slate-400" />
              </a>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4 justify-center">
            <Lock className="w-4 h-4" />
            <p>Aucun document accessible avec votre role</p>
          </div>
        )}

        {canGenerate && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="deployment-package-generator">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-900">Package deploiement VPS</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-700">v{packageInfo?.version || 'courante'}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">Genere automatiquement l'archive tar.gz de la version actuellement installee, sans .env, mots de passe, sauvegardes ni donnees runtime.</p>
                <div className="mt-2 flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-slate-500">
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />Python controle</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />React 18.3.1</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />Build production avant archive</span>
                </div>
                {packageInfo?.latest && (
                  <p className="text-[11px] text-slate-500 mt-2">Dernier package : <span className="font-medium text-slate-700">{packageInfo.latest.filename}</span> · {formatBytes(packageInfo.latest.size_bytes)}</p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap lg:justify-end">
                {packageInfo?.latest?.download_url && (
                  <a href={`${API}${packageInfo.latest.download_url}`} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50">
                    <Download className="w-4 h-4" />Dernier package
                  </a>
                )}
                <button type="button" onClick={generatePackage} disabled={generating}
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-wait">
                  <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                  {generating ? 'Verification & generation...' : 'Generer le package VPS'}
                </button>
              </div>
            </div>
            {generating && <p className="text-[11px] text-emerald-800 mt-3">Le build React complet est controle avant creation de l'archive. Cette operation peut prendre quelques minutes.</p>}
            {packageError && <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2"><AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span className="whitespace-pre-wrap">{packageError}</span></div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
